import { useEffect, useRef } from 'react';
import { useGT } from 'gt-react';
import { z } from 'zod';
import { TASK_STATUSES, type ColumnConfigEntry } from '@erp/shared-types';
import type { TaskPageResponse } from '@erp/rpc';
import { useTaskAnchorFieldCatalog } from 'src/api/rpc/hooks/tasks/useTaskAnchorFieldCatalog';
import { useFrontendTool } from '@/hooks/useFrontendTool';
import { useNamiSurface } from '@/hooks/nami/useNamiSurface';
import { labelsFor } from '@/hooks/nami/filterLabels';
import {
  useUrlFilterTools,
  type UrlFilterOption,
} from '@/hooks/nami/useUrlFilterTools';
import type { useTaskQueueController } from '../../../tasks/useTasksQueuePage';
import type { TasksQueueSearch } from '../../../tasks/taskQueueSearch';
import type { TaskPageTab } from '../taskPageTabs';
import {
  anchorFieldsContext,
  columnsContext,
  planHideColumn,
  planReferenceFilter,
  planShowAnchorColumn,
  type ShowColumnPlan,
  type TaskPageColumnState,
} from './taskPageNamiPlan';

type TaskQueue = ReturnType<typeof useTaskQueueController>;

const STATUS_OPTIONS: UrlFilterOption[] = TASK_STATUSES.map((status) => ({
  id: status,
  label: status.replace(/_/g, ' '),
}));

const showColumnSchema = z.object({
  path: z
    .string()
    .describe(
      "Field of the record the tasks work on, from `taskPage.anchorFields` in the on-screen context (e.g. 'supplierId' to show each task's supplier).",
    ),
  header: z
    .string()
    .optional()
    .describe('Column header; omit for the default (e.g. "Supplier").'),
});

const hideColumnSchema = z.object({
  column: z.string().describe('Header of a visible column.'),
});

const referenceFilterSchema = z.object({
  path: z
    .string()
    .describe(
      "A reference field from `taskPage.anchorFields` (one with `references`), e.g. 'supplierId'.",
    ),
  recordId: z
    .uuid()
    .nullable()
    .describe(
      "Id of the record this field references; it must match the field's `references` type. Null clears this filter.",
    ),
});

const searchSchema = z.object({
  value: z.string().describe('Text to search for. Empty clears the search.'),
});

/** Applies a show-column plan to the queue; returns the header shown. */
function applyShowColumn(queue: TaskQueue, plan: ShowColumnPlan): string {
  switch (plan.kind) {
    case 'already_visible':
      return plan.header;
    case 'unhide':
      queue.updateColumn(plan.columnId, { hidden: false });
      return plan.header;
    case 'add':
      queue.addColumn(plan.column);
      return plan.column.header;
  }
}

function useColumnTools(queue: TaskQueue, state: TaskPageColumnState) {
  const showColumn = useFrontendTool({
    name: 'show_task_page_column',
    description:
      "Show a field of each task's record as a column on this task page (e.g. the supplier of an invoice). Reference fields such as supplierId render as the record's name. Changes only this user's view.",
    parameters: showColumnSchema,
    readOnlyHint: true,
    enabled: state.canCustomizeColumns,
    execute: (input: z.infer<typeof showColumnSchema>) => ({
      shown: applyShowColumn(queue, planShowAnchorColumn(state, input)),
    }),
  });
  const hideColumn = useFrontendTool({
    name: 'hide_task_page_column',
    description:
      "Hide a column on this task page. Changes only this user's view.",
    parameters: hideColumnSchema,
    readOnlyHint: true,
    enabled: state.canCustomizeColumns,
    execute: ({ column }: z.infer<typeof hideColumnSchema>) => {
      const plan = planHideColumn(state, column);
      queue.updateColumn(plan.columnId, { hidden: true });
      return { hidden: plan.header };
    },
  });
  return [showColumn, hideColumn];
}

type QueueFilters = Pick<TasksQueueSearch, 'status' | 'assignee' | 'ref'>;

function pickFilters(search: TasksQueueSearch): QueueFilters {
  return { status: search.status, assignee: search.assignee, ref: search.ref };
}

/**
 * One live copy of the queue's filters for every tool on this surface. The
 * model can call several tools before React re-renders, and
 * `updateSearchParams` merges into the render-time URL, so each write sends
 * all filter keys from this copy: a status pick and a record pick in the same
 * turn both survive.
 */
function useLiveQueueFilters(queue: TaskQueue) {
  // Null until the first effect; reads fall back to the URL until then.
  const live = useRef<QueueFilters | null>(null);
  useEffect(() => {
    live.current = pickFilters(queue.searchParams);
  });
  const read = (): QueueFilters =>
    live.current ?? pickFilters(queue.searchParams);
  return {
    read,
    commit: (updates: Partial<QueueFilters>) => {
      live.current = { ...read(), ...updates };
      queue.updateSearchParams({ ...live.current }, true);
    },
  };
}

type LiveQueueFilters = ReturnType<typeof useLiveQueueFilters>;

function useQueueFilterTools(
  queue: TaskQueue,
  state: TaskPageColumnState,
  filters: LiveQueueFilters,
) {
  const referenceFilter = useFrontendTool({
    name: 'filter_task_page_by_record',
    description:
      "Show only tasks whose record points at one record through a reference field (e.g. invoices of one supplier: path 'supplierId'). Pass the referenced record's id: for a supplier reference resolve the name with search_suppliers first; for any other reference type use an id the user gave, never a supplier id. Also shows that field's column, since the filter lives on it.",
    parameters: referenceFilterSchema,
    readOnlyHint: true,
    execute: (input: z.infer<typeof referenceFilterSchema>) => {
      const plan = planReferenceFilter(state, filters.read().ref, input);
      if (plan.showColumn) applyShowColumn(queue, plan.showColumn);
      filters.commit({ ref: plan.ref });
      return { filtered: plan.path, recordId: input.recordId };
    },
  });
  const clear = useFrontendTool({
    name: 'clear_task_page_filters',
    description:
      'Remove the status, assignee, record and search filters on this task page.',
    parameters: z.object({}),
    readOnlyHint: true,
    execute: () => {
      filters.commit({
        status: undefined,
        assignee: undefined,
        ref: undefined,
      });
      queue.setSearchInput('');
      return { cleared: true };
    },
  });
  const search = useFrontendTool({
    name: 'search_task_page',
    description: "Search this task page's tasks by text (title and details).",
    parameters: searchSchema,
    readOnlyHint: true,
    execute: ({ value }: z.infer<typeof searchSchema>) => {
      // The search box owns the debounced URL sync; set it, not the URL.
      queue.setSearchInput(value);
      return { search: value };
    },
  });
  return [referenceFilter, search, clear];
}

function referenceFilterContext(
  queue: TaskQueue,
): Record<string, string> | undefined {
  const ref = queue.searchParams.ref;
  if (!ref) return undefined;
  const headers = new Map(
    queue.referenceColumns.map((column) => [column.path, column.header]),
  );
  return Object.fromEntries(
    Object.entries(ref).map(([path, id]) => [headers.get(path) ?? path, id]),
  );
}

/**
 * An org-configured task page as an agent surface: show/hide anchor columns
 * (the supplier of each invoice task), filter by a referenced record, status
 * and text, and the page's columns + available record fields as context.
 * Every tool changes only what this user sees.
 */
export function useTaskPageNamiSurface({
  page,
  tab,
  queue,
  anchorEntityType,
}: {
  page: TaskPageResponse;
  tab: TaskPageTab;
  queue: TaskQueue;
  anchorEntityType: Parameters<typeof useTaskAnchorFieldCatalog>[0];
}): void {
  const gt = useGT();
  const catalog = useTaskAnchorFieldCatalog(anchorEntityType);
  const columns: readonly ColumnConfigEntry[] = queue.configuredColumns;
  const state: TaskPageColumnState = {
    columns,
    catalog: anchorEntityType ? catalog.data?.fields : [],
    canCustomizeColumns: page.allowUserColumnCustomization,
  };

  const filters = useLiveQueueFilters(queue);
  const { tools: statusTools, activeFilters } =
    useUrlFilterTools<TasksQueueSearch>({
      search: queue.searchParams,
      // Status only: the other keys in `next` come from this hook's own copy,
      // which does not see a record pick made earlier in the same turn.
      onChange: (next) => filters.commit({ status: next.status }),
      fields: [
        {
          toolName: 'filter_task_page_by_status',
          description: 'Show only tasks in these statuses.',
          label: 'Status',
          kind: 'multiEnum',
          options: () => STATUS_OPTIONS,
          describe: (search) => labelsFor(search.status, STATUS_OPTIONS),
          apply: (values, prev) => ({
            ...prev,
            status: values?.filter((value) =>
              (TASK_STATUSES as readonly string[]).includes(value),
            ) as TasksQueueSearch['status'],
          }),
        },
      ],
    });
  const columnTools = useColumnTools(queue, state);
  const filterTools = useQueueFilterTools(queue, state, filters);

  const suggestionContext = 'AI assistant suggestion chip on a task page';
  useNamiSurface({
    moduleKey: 'tasks',
    pageKey: 'task-page',
    title: page.name,
    tools: [statusTools, columnTools, filterTools],
    suggestions: [
      gt('Show the supplier for each task', { context: suggestionContext }),
      gt('Only show tasks that need input', { context: suggestionContext }),
    ],
    placeholder: gt('Ask about this page…', {
      context: 'placeholder for the AI assistant input on a task page',
    }),
    getContext: () => ({
      filters: {
        ...activeFilters,
        ...(queue.searchParams.search
          ? { Search: queue.searchParams.search }
          : {}),
      },
      resultCount: queue.total ?? null,
      taskPage: {
        name: page.name,
        taskType: tab.typeName,
        recordType: anchorEntityType ?? null,
        columnsLocked: !page.allowUserColumnCustomization,
        // Fields before columns: the prompt's character budget cuts the end.
        anchorFields: anchorFieldsContext(state.catalog),
        columns: columnsContext(columns),
        recordFilters: referenceFilterContext(queue),
      },
    }),
  });
}
